import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    orderIndex: 0,
    isActive: true,
    image: null
  });
  const [preview, setPreview] = useState(null);

  const fetchBanners = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/banners/admin', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const formDataToSend = new FormData();
      
      if (formData.title) formDataToSend.append('title', formData.title);
      if (formData.link) formDataToSend.append('link', formData.link);
      formDataToSend.append('orderIndex', formData.orderIndex);
      formDataToSend.append('isActive', formData.isActive);
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      if (editingBanner) {
        await axios.put(`/api/banners/${editingBanner.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        await axios.post('/api/banners', formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      fetchBanners();
      resetForm();
    } catch (error) {
      console.error('Error saving banner:', error);
      alert(error.response?.data?.error || 'Banner kaydedilirken bir hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      link: '',
      orderIndex: 0,
      isActive: true,
      image: null
    });
    setPreview(null);
    setEditingBanner(null);
    setShowForm(false);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      link: banner.link || '',
      orderIndex: banner.orderIndex || banner.order_index || 0,
      isActive: banner.isActive !== undefined ? banner.isActive : (banner.is_active === 1 || banner.is_active === true),
      image: null
    });
    setPreview(banner.imageUrl || banner.image_url);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu bannerı silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/banners/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      alert('Banner silinirken bir hata oluştu');
    }
  };

  const handleOrderChange = async (id, direction) => {
    const banner = banners.find(b => b.id === id);
    if (!banner) return;

    const currentIndex = banner.orderIndex || banner.order_index || 0;
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Find banner at new index
    const targetBanner = banners.find(b => 
      (b.orderIndex || b.order_index || 0) === newIndex
    );

    if (!targetBanner && newIndex < 0) return;

    try {
      const token = localStorage.getItem('adminToken');
      
      // Update current banner
      await axios.put(`/api/banners/${id}`, {
        orderIndex: newIndex
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update target banner if exists
      if (targetBanner) {
        await axios.put(`/api/banners/${targetBanner.id}`, {
          orderIndex: currentIndex
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      fetchBanners();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gold">Banner Yönetimi</h2>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all duration-300 flex items-center gap-2"
        >
          <FaPlus />
          Yeni Banner Ekle
        </button>
      </div>

      {showForm && (
        <div className="bg-gradient-card rounded-xl p-6 border border-gold/30 mb-6">
          <h3 className="text-xl font-bold text-gold mb-4">
            {editingBanner ? 'Banner Düzenle' : 'Yeni Banner Ekle'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white/80 mb-2">Başlık (Opsiyonel)</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                placeholder="Banner başlığı"
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2">Link (Opsiyonel)</label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2">Sıra</label>
              <input
                type="number"
                value={formData.orderIndex}
                onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                min="0"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-5 h-5 text-gold rounded focus:ring-gold"
                />
                <span className="text-white/80">Aktif</span>
              </label>
            </div>

            <div>
              <label className="block text-white/80 mb-2">
                Banner Görseli {editingBanner ? '(Değiştirmek için seçin)' : '*'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                required={!editingBanner}
              />
              {preview && (
                <div className="mt-4">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-w-full h-auto rounded-lg border border-gold/30"
                    style={{ maxHeight: '300px' }}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition-all duration-300"
              >
                {editingBanner ? 'Güncelle' : 'Kaydet'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-navy-700 hover:bg-navy-600 text-white rounded-lg transition-all duration-300"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {banners.length === 0 ? (
          <div className="text-center text-white/60 py-12">
            <p className="text-xl">Henüz banner eklenmemiş.</p>
          </div>
        ) : (
          banners.map((banner) => (
            <div
              key={banner.id}
              className="bg-gradient-card rounded-xl p-6 border border-gold/30 flex flex-col md:flex-row items-start md:items-center gap-4"
            >
              <div className="flex-1">
                <div className="mb-2 flex items-center gap-3">
                  <span className="text-white/60 text-sm">Sıra: {banner.orderIndex || banner.order_index || 0}</span>
                  {banner.isActive === false || banner.is_active === 0 ? (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">Pasif</span>
                  ) : (
                    <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">Aktif</span>
                  )}
                </div>
                {banner.title && (
                  <h3 className="text-white font-semibold mb-2">{banner.title}</h3>
                )}
                {banner.link && (
                  <p className="text-white/60 text-sm mb-2">Link: {banner.link}</p>
                )}
                <div className="mt-2">
                  <img
                    src={banner.imageUrl || banner.image_url}
                    alt={banner.title || 'Banner'}
                    className="max-w-full h-auto rounded-lg border border-gold/30"
                    style={{ maxHeight: '200px' }}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOrderChange(banner.id, 'up')}
                    className="p-2 bg-navy-700 hover:bg-navy-600 text-white rounded-lg transition-colors"
                    title="Yukarı Taşı"
                  >
                    <FaArrowUp />
                  </button>
                  <button
                    onClick={() => handleOrderChange(banner.id, 'down')}
                    className="p-2 bg-navy-700 hover:bg-navy-600 text-white rounded-lg transition-colors"
                    title="Aşağı Taşı"
                  >
                    <FaArrowDown />
                  </button>
                </div>
                <button
                  onClick={() => handleEdit(banner)}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <FaEdit />
                  <span className="hidden md:inline">Düzenle</span>
                </button>
                <button
                  onClick={() => handleDelete(banner.id)}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <FaTrash />
                  <span className="hidden md:inline">Sil</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BannerManager;
